import React, { useState, useEffect } from "react";
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { 
  CheckIcon, 
  XMarkIcon, 
  ArrowPathIcon,
  ShieldCheckIcon,
  DocumentMagnifyingGlassIcon,
  InformationCircleIcon,
  BanknotesIcon,
  UserGroupIcon,
  ClockIcon,
  CalendarDaysIcon,
  DocumentCheckIcon,
  ChartBarIcon,
  PencilSquareIcon,
  TrophyIcon,
  ArrowUpCircleIcon,
  ChevronDownIcon,
  DocumentArrowDownIcon,
  TableCellsIcon,
  DocumentTextIcon,
  ArrowRightOnRectangleIcon,
  ArrowDownTrayIcon,
  MagnifyingGlassIcon,
  FunnelIcon
} from "@heroicons/react/24/outline";

// Import API functions
import { offboardingAPI } from '../services/employee.service';
import { offboardingNotificationAPI } from '../services/offboarding-notifications.service';

const HRTerminal = () => {
  const [requests, setRequests] = useState([]);
  const [filter, setFilter] = useState("all");
  const [activeTab, setActiveTab] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [selectedRequestId, setSelectedRequestId] = useState(null);
  const [dateFilter, setDateFilter] = useState({ from: '', to: '' });
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);

  // Reports States
  const [reportType, setReportType] = useState("attendance");
  const [reportPeriod, setReportPeriod] = useState("monthly");
  const [reportDateRange, setReportDateRange] = useState({ from: '', to: '' });
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);

  // Team Attendance States
  const [teamAttendance, setTeamAttendance] = useState([]);
  const [filteredAttendance, setFilteredAttendance] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [attendanceDateRange, setAttendanceDateRange] = useState({ from: '', to: '' });
  const [uniqueEmployees, setUniqueEmployees] = useState([]);
  const [attendanceSearchTerm, setAttendanceSearchTerm] = useState('');
  const [showAllRecords, setShowAllRecords] = useState(false);

  // Helper function to map request types to categories
  const mapCategory = (type = "") => {
    if (type.includes("LEAVE")) return "leave";
    if (type.includes("TIMESHEET")) return "timesheet";
    if (type.includes("ATTENDANCE")) return "attendance";
    if (type.includes("OVERTIME")) return "attendance";
    if (type.includes("REWARD")) return "incentive";
    if (type.includes("POLICY")) return "policy";
    return "all";
  };

  // 1. FETCH AND MAP DATA
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        if (activeTab === "team-attendance") {
          // HR should see ALL employees attendance, not just team
          const response = await fetch('http://localhost:8000/attendance/all-records?limit=500');
          if (!response.ok) throw new Error('Failed to fetch attendance');
          const data = await response.json();
          setTeamAttendance(data);
          const employees = [...new Set(data.map(r => r.emp_code))].map(code => {
            const record = data.find(r => r.emp_code === code);
            return { code, name: record.emp_name || code };
          });
          setUniqueEmployees(employees);
          setLoading(false);
          return;
        }
        
        let endpoint = "";
        let data = [];
        
        if (activeTab === "pending") {
          endpoint = "/unified_approval/admin/requests";
        } else if (activeTab === "offboarding") {
          // Fetch HR pending resignations
          try {
            const response = await offboardingAPI.getHRPendingApprovals();
            const resignationData = response.data?.pending_resignations || response.pending_resignations || [];
            
            const mappedData = resignationData.map(item => ({
              id: item.id,
              manager: "MANAGER",
              name: item.employee_name || item.emp_code,
              empId: item.emp_code,
              category: "offboarding",
              type: "FINAL_RESIGNATION",
              details: `Final Resignation: ${item.reason} | LWD: ${item.requested_lwd} | Handover: ${item.handover_completed ? 'Yes' : 'No'} | Assets: ${item.assets_confirmed ? 'Confirmed' : 'Pending'}`,
              amount: 0,
              hasReceipt: false,
              date: item.submitted_at
                ? new Date(item.submitted_at).toLocaleDateString()
                : "—",
              priority: "High",
              status: "PENDING_HR_APPROVAL",
              reason: null,
              resignationData: item // Store full resignation data for HR actions
            }));
            
            setRequests(mappedData);
            setLoading(false);
            return;
          } catch (error) {
            console.error('Failed to fetch HR resignations:', error);
            setRequests([]);
            setLoading(false);
            return;
          }
        } else {
          endpoint = "/unified_approval/admin/requests/history";
        }
        
        const response = await fetch(endpoint);
        
        if (!response.ok) {
            throw new Error(`Fetch failed with status: ${response.status}`);
        }
        
        data = await response.json();
        
        // LOGIC FIX: Map the backend data to generic approval_requests format
        const mappedData = data.map(item => {
          let details = item.employee_reason || "—";
          let amount = 0;
          let hasReceipt = false;
          
          // Extract details from payload if available
          if (item.payload) {
            try {
              const payload = typeof item.payload === 'string' ? JSON.parse(item.payload) : item.payload;
              if (payload.reason) details = payload.reason;
              if (payload.hours) {
                details += ` (${payload.hours} hours)`;
                amount = payload.hours * 500; // Estimate overtime amount
              }
              if (payload.date) details += ` - ${payload.date}`;
              if (payload.amount) {
                amount = payload.amount;
                details = `${payload.category || 'Expense'}: ₹${payload.amount} - ${payload.description}`;
              }
              if (payload.receipt_url) hasReceipt = true;
            } catch (e) {
              // Keep original details if payload parsing fails
            }
          }
          
          return {
            id: item.id,
            manager: item.manager_emp_code || "MANAGER",
            name: item.employee_name || item.emp_code,
            empId: item.emp_code,
            category: mapCategory(item.request_type),
            type: item.request_type,
            details: details,
            amount: amount,
            hasReceipt: hasReceipt,
            date: item.submitted_at
              ? new Date(item.submitted_at).toLocaleDateString()
              : "—",
            priority: "Normal",
            status: item.status,
            reason: item.admin_reason || item.hr_reason
          };
        });

        // Filter based on active tab
        let filteredData = mappedData;
        if (activeTab === "approved") {
          filteredData = mappedData.filter(item => item.status === "HR_APPROVED");
        } else if (activeTab === "rejected") {
          filteredData = mappedData.filter(item => item.status === "HR_REJECTED");
        }

        setRequests(filteredData);
      } catch (error) {
        console.error("Error fetching HR data:", error);
        setRequests([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [activeTab, dateFilter]);

  useEffect(() => {
    if (activeTab === "team-attendance") {
      applyAttendanceFilters();
    }
  }, [teamAttendance, selectedEmployees, attendanceDateRange, showAllRecords]);

  const applyAttendanceFilters = () => {
    let filtered = [...teamAttendance];
    if (!showAllRecords) {
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      filtered = filtered.filter(r => new Date(r.date) >= sevenDaysAgo);
    }
    if (selectedEmployees.length > 0) {
      filtered = filtered.filter(r => selectedEmployees.includes(r.emp_code));
    }
    if (attendanceDateRange.from) {
      filtered = filtered.filter(r => new Date(r.date) >= new Date(attendanceDateRange.from));
    }
    if (attendanceDateRange.to) {
      filtered = filtered.filter(r => new Date(r.date) <= new Date(attendanceDateRange.to));
    }
    setFilteredAttendance(filtered);
  };

  const toggleEmployeeSelection = (empCode) => {
    setSelectedEmployees(prev =>
      prev.includes(empCode) ? prev.filter(e => e !== empCode) : [...prev, empCode]
    );
  };

  const downloadAttendanceExcel = () => {
    const dataToExport = filteredAttendance.length > 0 ? filteredAttendance : teamAttendance;
    const metadataRows = [
      { 'Report Information': 'Report Type', 'Value': 'Team Attendance Records' },
      { 'Report Information': 'Generated By', 'Value': localStorage.getItem("emp_code") || "ADMIN" },
      { 'Report Information': 'Generation Time', 'Value': new Date().toLocaleString() },
      { 'Report Information': 'Total Records', 'Value': dataToExport.length },
      { 'Report Information': '', 'Value': '' }
    ];
    const headerRow = {
      'Employee Code': 'Employee Code',
      'Employee Name': 'Employee Name',
      'Date': 'Date',
      'Check-In': 'Check-In',
      'Check-Out': 'Check-Out',
      'Work Hours': 'Work Hours',
      'Status': 'Status'
    };
    const dataRows = dataToExport.map(record => ({
      'Employee Code': record.emp_code,
      'Employee Name': record.emp_name,
      'Date': record.date,
      'Check-In': record.check_in_time,
      'Check-Out': record.check_out_time,
      'Work Hours': record.work_hours,
      'Status': record.status
    }));
    const allRows = [...metadataRows, headerRow, ...dataRows];
    const ws = XLSX.utils.json_to_sheet(allRows);
    ws['!cols'] = [{ wch: 20 }, { wch: 25 }, { wch: 15 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 12 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Team Attendance');
    XLSX.writeFile(wb, `team_attendance_${new Date().getTime()}.xlsx`);
  };

  // 2. ACTION HANDLER - NOW ENABLED
  const handleFinalAction = async (id, action) => {
    if (action === 'reject') {
      setSelectedRequestId(id);
      setShowRejectModal(true);
      return;
    }

    try {
      let endpoint = '';
      let response;
      
      if (activeTab === 'offboarding') {
        // Use offboarding API for HR resignation approval
        const decision = { approve: action === 'approve' };
        response = await offboardingAPI.hrApproveResignation(id, decision);
        
        if (response.data || response.message) {
          // Get resignation data for notifications
          const resignationData = requests.find(req => req.id === id)?.resignationData;
          
          // Send notifications
          if (resignationData) {
            await offboardingNotificationAPI.notifyHRDecision(
              resignationData.emp_code,
              'MANAGER001', // You might want to get this from the resignation data
              true,
              resignationData
            );
          }
          
          setRequests(prev => prev.filter(req => req.id !== id));
          alert(`Resignation successfully ${action === 'approve' ? 'Approved - Employee will receive final documents' : 'Rejected'}.`);
        } else {
          throw new Error('Invalid response');
        }
      } else {
        endpoint = `/unified_approval/admin/requests/${id}/${action}`;
        response = await fetch(endpoint, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' }
        });

        if (response.ok) {
          setRequests(prev => prev.filter(req => req.id !== id));
          alert(`Request successfully ${action === 'approve' ? 'Approved' : 'Rejected'}.`);
        } else {
          const errorData = await response.json();
          alert(`Error: ${errorData.detail || "Server error"}`);
        }
      }
    } catch (e) {
      console.error('Action failed:', e);
      alert("Failed to process request. Please try again.");
    }
  };

  const handleRejectSubmit = async () => {
    if (!rejectReason.trim()) {
      alert("Please enter a rejection reason");
      return;
    }

    try {
      if (activeTab === 'offboarding') {
        // Use offboarding API for HR resignation rejection
        const decision = { approve: false, reason: rejectReason };
        const response = await offboardingAPI.hrApproveResignation(selectedRequestId, decision);
        
        if (response.data || response.message) {
          // Get resignation data for notifications
          const resignationData = requests.find(req => req.id === selectedRequestId)?.resignationData;
          
          // Send notifications
          if (resignationData) {
            await offboardingNotificationAPI.notifyHRDecision(
              resignationData.emp_code,
              'MANAGER001', // You might want to get this from the resignation data
              false,
              { ...resignationData, rejection_reason: rejectReason }
            );
          }
          
          setRequests(prev => prev.filter(req => req.id !== selectedRequestId));
          alert("Resignation rejected and returned to employee and manager");
          setShowRejectModal(false);
          setRejectReason("");
          setSelectedRequestId(null);
        } else {
          throw new Error('Invalid response');
        }
      } else {
        const endpoint = `/unified_approval/admin/requests/${selectedRequestId}/reject`;
        const response = await fetch(endpoint, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ reason: rejectReason })
        });

        if (response.ok) {
          setRequests(prev => prev.filter(req => req.id !== selectedRequestId));
          alert("Request rejected and returned to manager");
          setShowRejectModal(false);
          setRejectReason("");
          setSelectedRequestId(null);
        } else {
          const errorData = await response.json();
          alert(`Error: ${errorData.detail || "Server error"}`);
        }
      }
    } catch (e) {
      console.error('Rejection failed:', e);
      alert("Failed to process rejection. Please try again.");
    }
  };

  const downloadTimesheet = async (requestId, format) => {
    try {
      const response = await fetch(`/unified_approval/timesheet/download/${requestId}?format=${format}`);
      
      if (response.status === 404) {
        alert('Timesheet download not available. This timesheet may not have detailed data.');
        return;
      }
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `timesheet_${requestId}.${format === 'excel' ? 'xlsx' : 'pdf'}`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        alert('Download not available');
      }
    } catch (err) {
      console.error('Download error:', err);
      alert('Download not available for this timesheet');
    }
  };

  const filtered = requests.filter(r => filter === "all" || r.category === filter);

  const getCategoryIcon = (cat) => {
    switch(cat) {
      case 'expense': return <BanknotesIcon className="h-4 w-4 text-emerald-600" />;
      case 'incentive': return <TrophyIcon className="h-4 w-4 text-purple-600" />;
      case 'promotion': return <ArrowUpCircleIcon className="h-4 w-4 text-amber-600" />;
      case 'attendance': return <ClockIcon className="h-4 w-4 text-blue-600" />;
      case 'leave': return <CalendarDaysIcon className="h-4 w-4 text-orange-600" />;
      case 'correction': return <PencilSquareIcon className="h-4 w-4 text-rose-600" />;
      case 'timesheet': return <DocumentCheckIcon className="h-4 w-4 text-indigo-600" />;
      case 'appraisal': return <ChartBarIcon className="h-4 w-4 text-cyan-600" />;
      case 'policy': return <ShieldCheckIcon className="h-4 w-4 text-blue-600" />;
      case 'offboarding': return <UserGroupIcon className="h-4 w-4 text-red-600" />;
      default: return <DocumentMagnifyingGlassIcon className="h-4 w-4 text-slate-400" />;
    }
  };

  const generateReport = async (format) => {
    setIsGeneratingReport(true);
    try {
      let fromDate, toDate;
      
      if (reportPeriod === 'custom' && reportDateRange.from && reportDateRange.to) {
        fromDate = reportDateRange.from;
        toDate = reportDateRange.to;
      } else {
        const now = new Date();
        
        switch (reportPeriod) {
          case 'daily':
            fromDate = toDate = now.toISOString().split('T')[0];
            break;
          case 'monthly':
            fromDate = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
            toDate = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];
            break;
          case 'quarterly':
            const quarter = Math.floor(now.getMonth() / 3);
            fromDate = new Date(now.getFullYear(), quarter * 3, 1).toISOString().split('T')[0];
            toDate = new Date(now.getFullYear(), quarter * 3 + 3, 0).toISOString().split('T')[0];
            break;
          case 'halfyearly':
            const half = now.getMonth() < 6 ? 0 : 6;
            fromDate = new Date(now.getFullYear(), half, 1).toISOString().split('T')[0];
            toDate = new Date(now.getFullYear(), half + 6, 0).toISOString().split('T')[0];
            break;
          case 'yearly':
            fromDate = new Date(now.getFullYear(), 0, 1).toISOString().split('T')[0];
            toDate = new Date(now.getFullYear(), 11, 31).toISOString().split('T')[0];
            break;
          default:
            fromDate = toDate = now.toISOString().split('T')[0];
        }
      }
      
      // Get current user info (you may need to get this from auth context)
      const currentUser = localStorage.getItem('emp_code') || 'ADMIN001';
      const generationTime = new Date().toLocaleString();
      
      // Filter data based on report type and date range
      const filteredData = requests.filter(req => {
        const matchesType = reportType === 'all' || req.category === reportType;
        const reqDate = new Date(req.date);
        const matchesDate = reqDate >= new Date(fromDate) && reqDate <= new Date(toDate);
        return matchesType && matchesDate;
      });
      
      if (format === 'excel') {
        // Create metadata sheet
        const metadataSheet = [
          ['Report Information', ''],
          ['Report Type', reportType.charAt(0).toUpperCase() + reportType.slice(1)],
          ['Report Period', reportPeriod.charAt(0).toUpperCase() + reportPeriod.slice(1)],
          ['Date Range', `${fromDate} to ${toDate}`],
          ['Generated By', currentUser],
          ['Generation Time', generationTime],
          ['Total Records', filteredData.length],
          ['', ''],
          ['Data Summary', '']
        ];
        
        // Create data sheet with tab-specific headers
        let headerRow, dataRows;
        
        // Define headers based on report type to match the UI table structure
        if (reportType === 'attendance') {
          headerRow = {
            'Reporting Manager': 'Reporting Manager',
            'Employee Details': 'Employee Details', 
            'Request Category': 'Request Category',
            'Data Summary': 'Data Summary',
            'Priority': 'Priority',
            'Final Action': 'Final Action'
          };
          dataRows = filteredData.map(req => ({
            'Reporting Manager': req.manager,
            'Employee Details': `${req.name} (${req.empId})`,
            'Request Category': `${req.category} - ${req.type}`,
            'Data Summary': `${req.details} - Amount: ₹${req.amount} - ${req.date}`,
            'Priority': req.priority,
            'Final Action': req.status
          }));
        } else if (reportType === 'leave') {
          headerRow = {
            'Employee Details': 'Employee Details',
            'Leave Type': 'Leave Type',
            'Duration': 'Duration', 
            'Reason': 'Reason',
            'Status': 'Status',
            'Action': 'Action'
          };
          dataRows = filteredData.map(req => ({
            'Employee Details': `${req.name} (${req.empId})`,
            'Leave Type': req.type,
            'Duration': req.details,
            'Reason': req.details,
            'Status': req.status,
            'Action': req.reason || 'PROCESSED'
          }));
        } else if (reportType === 'timesheet') {
          headerRow = {
            'Employee Details': 'Employee Details',
            'Project/Task': 'Project/Task',
            'Hours Logged': 'Hours Logged',
            'Date Range': 'Date Range',
            'Status': 'Status',
            'Action': 'Action'
          };
          dataRows = filteredData.map(req => ({
            'Employee Details': `${req.name} (${req.empId})`,
            'Project/Task': req.type,
            'Hours Logged': req.details,
            'Date Range': req.date,
            'Status': req.status,
            'Action': req.reason || 'PROCESSED'
          }));
        } else if (reportType === 'expense') {
          headerRow = {
            'Employee Details': 'Employee Details',
            'Expense Category': 'Expense Category',
            'Amount': 'Amount',
            'Description': 'Description',
            'Status': 'Status',
            'Action': 'Action'
          };
          dataRows = filteredData.map(req => ({
            'Employee Details': `${req.name} (${req.empId})`,
            'Expense Category': req.type,
            'Amount': `₹${req.amount}`,
            'Description': req.details,
            'Status': req.status,
            'Action': req.reason || 'PROCESSED'
          }));
        } else if (reportType === 'incentive') {
          headerRow = {
            'Employee Details': 'Employee Details',
            'Reward Type': 'Reward Type',
            'Amount': 'Amount',
            'Reason': 'Reason',
            'Status': 'Status',
            'Action': 'Action'
          };
          dataRows = filteredData.map(req => ({
            'Employee Details': `${req.name} (${req.empId})`,
            'Reward Type': req.type,
            'Amount': `₹${req.amount}`,
            'Reason': req.details,
            'Status': req.status,
            'Action': req.reason || 'PROCESSED'
          }));
        } else if (reportType === 'policy') {
          headerRow = {
            'Employee Name': 'Employee Name',
            'Policy Document': 'Policy Document',
            'Signed Date': 'Signed Date',
            'Status': 'Status'
          };
          dataRows = filteredData.map(req => ({
            'Employee Name': req.name,
            'Policy Document': req.type,
            'Signed Date': req.date,
            'Status': req.status
          }));
        } else {
          // Default structure for other report types
          headerRow = {
            'Reporting Manager': 'Reporting Manager',
            'Employee Details': 'Employee Details',
            'Request Category': 'Request Category',
            'Data Summary': 'Data Summary',
            'Priority': 'Priority',
            'Final Action': 'Final Action'
          };
          dataRows = filteredData.map(req => ({
            'Reporting Manager': req.manager,
            'Employee Details': `${req.name} (${req.empId})`,
            'Request Category': `${req.category} - ${req.type}`,
            'Data Summary': `${req.details} - Amount: ₹${req.amount} - ${req.date}`,
            'Priority': req.priority,
            'Final Action': req.status
          }));
        }
        
        const wb = XLSX.utils.book_new();
        
        // Add metadata sheet
        const wsMetadata = XLSX.utils.aoa_to_sheet(metadataSheet);
        XLSX.utils.book_append_sheet(wb, wsMetadata, 'Report Info');
        
        // Add data sheet with appropriate headers
        const allDataRows = [headerRow, ...dataRows];
        const wsData = XLSX.utils.json_to_sheet(allDataRows);
        XLSX.utils.book_append_sheet(wb, wsData, 'HR Report Data');
        
        XLSX.writeFile(wb, `${reportType}_${reportPeriod}_report_${new Date().toISOString().split('T')[0]}.xlsx`);
      } else {
        const doc = new jsPDF();
        
        // Header with metadata
        doc.setFontSize(18);
        doc.setFont('helvetica', 'bold');
        doc.text(`HR ${reportType.toUpperCase()} Report`, 20, 25);
        
        doc.setFontSize(12);
        doc.setFont('helvetica', 'normal');
        doc.text(`Report Type: ${reportType.charAt(0).toUpperCase() + reportType.slice(1)}`, 20, 40);
        doc.text(`Report Period: ${reportPeriod.charAt(0).toUpperCase() + reportPeriod.slice(1)}`, 20, 50);
        doc.text(`Date Range: ${fromDate} to ${toDate}`, 20, 60);
        doc.text(`Generated By: ${currentUser}`, 20, 70);
        doc.text(`Generation Time: ${generationTime}`, 20, 80);
        doc.text(`Total Records: ${filteredData.length}`, 20, 90);
        
        // Add a line separator
        doc.setLineWidth(0.5);
        doc.line(20, 95, 190, 95);
        
        // Define PDF table structure based on report type
        let tableHeaders, tableData;
        
        if (reportType === 'attendance') {
          tableHeaders = ['Manager', 'Employee', 'Category', 'Summary', 'Priority', 'Action'];
          tableData = filteredData.map(req => [
            req.manager,
            `${req.name} (${req.empId})`,
            req.category,
            req.details.substring(0, 25) + (req.details.length > 25 ? '...' : ''),
            req.priority,
            req.status
          ]);
        } else if (reportType === 'leave') {
          tableHeaders = ['Employee', 'Leave Type', 'Duration', 'Reason', 'Status'];
          tableData = filteredData.map(req => [
            `${req.name} (${req.empId})`,
            req.type,
            req.details.substring(0, 20) + '...',
            req.details.substring(0, 25) + '...',
            req.status
          ]);
        } else if (reportType === 'timesheet') {
          tableHeaders = ['Employee', 'Project/Task', 'Hours', 'Date', 'Status'];
          tableData = filteredData.map(req => [
            `${req.name} (${req.empId})`,
            req.type,
            req.details.substring(0, 15) + '...',
            req.date,
            req.status
          ]);
        } else if (reportType === 'expense') {
          tableHeaders = ['Employee', 'Category', 'Amount', 'Description', 'Status'];
          tableData = filteredData.map(req => [
            `${req.name} (${req.empId})`,
            req.type,
            `₹${req.amount}`,
            req.details.substring(0, 20) + '...',
            req.status
          ]);
        } else if (reportType === 'incentive') {
          tableHeaders = ['Employee', 'Reward Type', 'Amount', 'Reason', 'Status'];
          tableData = filteredData.map(req => [
            `${req.name} (${req.empId})`,
            req.type,
            `₹${req.amount}`,
            req.details.substring(0, 20) + '...',
            req.status
          ]);
        } else if (reportType === 'policy') {
          tableHeaders = ['Employee Name', 'Policy Document', 'Signed Date', 'Status'];
          tableData = filteredData.map(req => [
            req.name,
            req.type,
            req.date,
            req.status
          ]);
        } else {
          // Default structure
          tableHeaders = ['Manager', 'Employee', 'Category', 'Summary', 'Priority', 'Action'];
          tableData = filteredData.map(req => [
            req.manager,
            `${req.name} (${req.empId})`,
            req.category,
            req.details.substring(0, 25) + (req.details.length > 25 ? '...' : ''),
            req.priority,
            req.status
          ]);
        }
        
        doc.autoTable({
          head: [tableHeaders],
          body: tableData,
          startY: 105,
          styles: { fontSize: 8 },
          headStyles: { fillColor: [59, 130, 246] },
          margin: { top: 105 }
        });
        
        doc.save(`${reportType}_${reportPeriod}_report_${new Date().toISOString().split('T')[0]}.pdf`);
      }
    } catch (err) {
      console.error('Report generation error:', err);
      alert('Report generation failed');
    } finally {
      setIsGeneratingReport(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f8fafc] p-4 md:p-8 font-roboto text-slate-800">
      <style dangerouslySetInnerHTML={{ __html: `
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@500;600;700&family=Roboto:wght@400;500;700&display=swap');
        .font-poppins { font-family: 'Poppins', sans-serif; }
        .font-roboto { font-family: 'Roboto', sans-serif; }
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .animate-spin-slow { animation: spin 3s linear infinite; }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}} />

      <div className="max-w-[1600px] mx-auto">
        {/* HEADER AREA */}
        <div className="flex flex-col xl:flex-row justify-between xl:items-end mb-8 border-b border-slate-200 pb-6 gap-6">
          <div className="flex items-center gap-4">
            <div className="bg-slate-900 p-2 rounded shadow-lg">
                <ShieldCheckIcon className="h-7 w-7 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold font-poppins text-slate-900 uppercase tracking-tight">HR Processing Terminal</h1>
              <p className="text-[10px] font-bold text-blue-600 uppercase tracking-[0.2em]">Final Authorization • Compliance Audit • Lifecycle Management</p>
            </div>
          </div>

          {/* HR TABS */}
          <div className="flex bg-white border border-slate-200 p-1 rounded-md shadow-sm">
            {[
              {id: "pending", label: "Pending Requests"}, 
              {id: "approved", label: "My Approvals"}, 
              {id: "rejected", label: "My Rejections"},
              {id: "offboarding", label: "Employee Exits"},
              {id: "team-attendance", label: "Team Attendance"},
              {id: "reports", label: "Reports"}
            ].map((t) => (
              <button
                key={t.id}
                onClick={() => setActiveTab(t.id)}
                className={`px-4 py-2 rounded text-[10px] font-bold font-poppins uppercase whitespace-nowrap transition-all ${
                  activeTab === t.id ? "bg-blue-600 text-white shadow-md" : "text-slate-400 hover:text-slate-600 hover:bg-slate-50"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {/* DATE FILTER FOR HISTORY */}
          {(activeTab === "approved" || activeTab === "rejected") && (
            <div className="flex gap-2 items-center">
              <input
                type="date"
                value={dateFilter.from}
                onChange={(e) => setDateFilter(prev => ({ ...prev, from: e.target.value }))}
                className="border rounded px-2 py-1 text-xs"
                placeholder="From"
              />
              <span className="text-xs text-slate-500">to</span>
              <input
                type="date"
                value={dateFilter.to}
                onChange={(e) => setDateFilter(prev => ({ ...prev, to: e.target.value }))}
                className="border rounded px-2 py-1 text-xs"
                placeholder="To"
              />
            </div>
          )}

          {/* CATEGORY DROPDOWN */}
          {activeTab === "pending" && (
            <div className="relative">
              <button
                onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
                className="flex items-center gap-2 bg-white border border-slate-200 px-4 py-2 rounded-md shadow-sm text-[10px] font-bold font-poppins uppercase text-slate-600 hover:bg-slate-50"
              >
                {filter === "all" ? "All Categories" : 
                 filter === "attendance" ? "Attendance" :
                 filter === "correction" ? "Corrections" :
                 filter === "leave" ? "Leaves" :
                 filter === "timesheet" ? "Timesheets" :
                 filter === "expense" ? "Reimbursements" :
                 filter === "incentive" ? "Bonus & Rewards" :
                 filter === "appraisal" ? "Appraisal" :
                 filter === "promotion" ? "Promotion/Hike" :
                 filter === "policy" ? "Policy Compliance" : "All Categories"}
                <ChevronDownIcon className={`h-4 w-4 transition-transform ${showCategoryDropdown ? 'rotate-180' : ''}`} />
              </button>
              
              {showCategoryDropdown && (
                <div className="absolute top-full left-0 mt-1 bg-white border border-slate-200 rounded-md shadow-lg z-10 min-w-[200px]">
                  {[
                    {id: "all", label: "All Items"}, 
                    {id: "attendance", label: "Attendance"}, 
                    {id: "correction", label: "Correction Requests"},
                    {id: "leave", label: "Leaves"},
                    {id: "timesheet", label: "Timesheets"},
                    {id: "expense", label: "Reimbursements"}, 
                    {id: "incentive", label: "Bonus & Rewards"}, 
                    {id: "appraisal", label: "Appraisal"},
                    {id: "promotion", label: "Promotion/Hike"},
                    {id: "policy", label: "Policy Compliance"}
                  ].map((t) => (
                    <button
                      key={t.id}
                      onClick={() => {
                        setFilter(t.id);
                        setShowCategoryDropdown(false);
                      }}
                      className={`w-full text-left px-4 py-2 text-[10px] font-bold font-poppins uppercase hover:bg-slate-50 transition-all ${
                        filter === t.id ? "bg-blue-50 text-blue-600" : "text-slate-600"
                      }`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* TABLE */}
        {activeTab === "team-attendance" ? (
          <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6">
            <div className="flex justify-between items-center mb-6">
              <div className="flex items-center gap-4">
                <h3 className="text-lg font-700 font-poppins text-slate-800">Team Attendance Records</h3>
                <button onClick={() => setShowAllRecords(!showAllRecords)} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-bold">
                  {showAllRecords ? 'Show Last 7 Days' : 'View All'}
                </button>
              </div>
              <button onClick={downloadAttendanceExcel} className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-bold">
                <ArrowDownTrayIcon className="h-5 w-5" />Download Excel
              </button>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
              <div className="lg:col-span-1 bg-slate-50 rounded-lg border p-4">
                <h4 className="text-sm font-bold text-slate-700 mb-4">Filters</h4>
                <div className="space-y-4">
                  <div>
                    <label className="text-xs font-bold text-slate-600 mb-2 block">Date Range</label>
                    <input type="date" value={attendanceDateRange.from} onChange={(e) => setAttendanceDateRange(prev => ({ ...prev, from: e.target.value }))} className="w-full border rounded p-2 text-sm mb-2" />
                    <input type="date" value={attendanceDateRange.to} onChange={(e) => setAttendanceDateRange(prev => ({ ...prev, to: e.target.value }))} className="w-full border rounded p-2 text-sm" />
                  </div>
                  <div>
                    <label className="text-xs font-bold text-slate-600 mb-2 block">Select Employees</label>
                    <div className="relative mb-2">
                      <input type="text" placeholder="Search..." value={attendanceSearchTerm} onChange={(e) => setAttendanceSearchTerm(e.target.value)} className="w-full pl-3 pr-3 py-2 border rounded text-sm" />
                    </div>
                    <div className="max-h-64 overflow-y-auto border rounded p-2 space-y-1 bg-white">
                      {uniqueEmployees.filter(emp => emp.code.toLowerCase().includes(attendanceSearchTerm.toLowerCase()) || emp.name.toLowerCase().includes(attendanceSearchTerm.toLowerCase())).map(emp => (
                        <label key={emp.code} className="flex items-center gap-2 p-2 hover:bg-slate-50 rounded cursor-pointer">
                          <input type="checkbox" checked={selectedEmployees.includes(emp.code)} onChange={() => toggleEmployeeSelection(emp.code)} className="rounded" />
                          <span className="text-sm">{emp.name} ({emp.code})</span>
                        </label>
                      ))}
                    </div>
                    {selectedEmployees.length > 0 && (
                      <button onClick={() => setSelectedEmployees([])} className="mt-2 text-xs text-red-600 hover:underline">Clear Selection ({selectedEmployees.length})</button>
                    )}
                  </div>
                </div>
              </div>
              <div className="lg:col-span-3">
                <div className="bg-white rounded-lg border overflow-hidden">
                  <div className="p-4 border-b bg-slate-50">
                    <h4 className="text-sm font-bold text-slate-700">Records ({(filteredAttendance.length > 0 ? filteredAttendance : teamAttendance).length})</h4>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full">
                      <thead className="bg-slate-50 text-slate-700 text-xs font-bold uppercase">
                        <tr>
                          <th className="py-3 px-4 text-left">Employee Code</th>
                          <th className="py-3 px-4 text-left">Employee Name</th>
                          <th className="py-3 px-4 text-left">Date</th>
                          <th className="py-3 px-4 text-left">Check-In</th>
                          <th className="py-3 px-4 text-left">Check-Out</th>
                          <th className="py-3 px-4 text-left">Work Hours</th>
                          <th className="py-3 px-4 text-center">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {(filteredAttendance.length > 0 ? filteredAttendance : teamAttendance).map((record, idx) => (
                          <tr key={idx} className="hover:bg-blue-50/30">
                            <td className="py-3 px-4 text-sm font-bold text-slate-900">{record.emp_code}</td>
                            <td className="py-3 px-4 text-sm text-slate-700">{record.emp_name}</td>
                            <td className="py-3 px-4 text-sm text-slate-600">{record.date}</td>
                            <td className="py-3 px-4 text-sm text-slate-600">{record.check_in_time}</td>
                            <td className="py-3 px-4 text-sm text-slate-600">{record.check_out_time}</td>
                            <td className="py-3 px-4 text-sm text-slate-600">{record.work_hours}</td>
                            <td className="py-3 px-4 text-center">
                              <span className={`text-xs font-bold px-3 py-1 rounded-full ${
                                record.status === 'PRESENT' ? 'bg-green-100 text-green-700' :
                                record.status === 'HALF_DAY' ? 'bg-yellow-100 text-yellow-700' :
                                'bg-red-100 text-red-700'
                              }`}>{record.status}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : activeTab === "reports" ? (
          <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6">
            <div className="max-w-4xl mx-auto">
              <h3 className="text-lg font-700 font-poppins text-slate-800 mb-6 flex items-center gap-2">
                <ChartBarIcon className="h-6 w-6 text-blue-600" />
                Generate HR Reports
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="space-y-2">
                  <label className="text-sm font-bold text-slate-700 uppercase tracking-wide">Report Type</label>
                  <select 
                    value={reportType} 
                    onChange={(e) => setReportType(e.target.value)}
                    className="w-full border border-slate-300 rounded-lg p-3 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="attendance">Attendance Correction Requests</option>
                    <option value="leave">Leaves</option>
                    <option value="timesheet">Timesheets</option>
                    <option value="expense">Reimbursements</option>
                    <option value="incentive">Bonus & Rewards</option>
                    <option value="appraisal">Appraisal</option>
                    <option value="promotion">Promotion/Hike</option>
                    <option value="policy">Policy Compliance</option>
                  </select>
                </div>
                
                <div className="space-y-2">
                  <label className="text-sm font-bold text-slate-700 uppercase tracking-wide">Report Period</label>
                  <select 
                    value={reportPeriod} 
                    onChange={(e) => setReportPeriod(e.target.value)}
                    className="w-full border border-slate-300 rounded-lg p-3 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="daily">Daily</option>
                    <option value="monthly">Monthly</option>
                    <option value="quarterly">Quarterly</option>
                    <option value="halfyearly">Half Yearly</option>
                    <option value="yearly">Yearly</option>
                    <option value="custom">Custom Date Range</option>
                  </select>
                </div>
                
                <div className="space-y-2">
                  <label className="text-sm font-bold text-slate-700 uppercase tracking-wide">Download Format</label>
                  <div className="flex gap-3">
                    <button 
                      onClick={() => generateReport('excel')}
                      disabled={isGeneratingReport}
                      className="flex-1 bg-green-600 hover:bg-green-700 disabled:bg-slate-400 text-white px-4 py-3 rounded-lg text-sm font-bold uppercase tracking-wide transition-colors flex items-center justify-center gap-2"
                    >
                      <TableCellsIcon className="h-5 w-5" />
                      {isGeneratingReport ? 'Generating...' : 'Excel'}
                    </button>
                    <button 
                      onClick={() => generateReport('pdf')}
                      disabled={isGeneratingReport}
                      className="flex-1 bg-red-600 hover:bg-red-700 disabled:bg-slate-400 text-white px-4 py-3 rounded-lg text-sm font-bold uppercase tracking-wide transition-colors flex items-center justify-center gap-2"
                    >
                      <DocumentTextIcon className="h-5 w-5" />
                      {isGeneratingReport ? 'Generating...' : 'PDF'}
                    </button>
                  </div>
                </div>
              </div>
              
              {reportPeriod === 'custom' && (
                <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 mb-6">
                  <h4 className="text-sm font-bold text-slate-700 mb-3 uppercase tracking-wide">Custom Date Range</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-xs font-bold text-slate-600 uppercase">From Date</label>
                      <input 
                        type="date" 
                        value={reportDateRange.from}
                        onChange={(e) => setReportDateRange(prev => ({ ...prev, from: e.target.value }))}
                        className="w-full border border-slate-300 rounded-lg p-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-xs font-bold text-slate-600 uppercase">To Date</label>
                      <input 
                        type="date" 
                        value={reportDateRange.to}
                        onChange={(e) => setReportDateRange(prev => ({ ...prev, to: e.target.value }))}
                        className="w-full border border-slate-300 rounded-lg p-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>
                </div>
              )}
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="text-sm font-bold text-blue-800 mb-2 flex items-center gap-2">
                  <DocumentTextIcon className="h-5 w-5" />
                  Report Information
                </h4>
                <div className="text-sm text-blue-700 space-y-1">
                  <p><span className="font-bold">Report Type:</span> {reportType.charAt(0).toUpperCase() + reportType.slice(1)}</p>
                  <p><span className="font-bold">Period:</span> {reportPeriod.charAt(0).toUpperCase() + reportPeriod.slice(1)}</p>
                  {reportPeriod === 'custom' && reportDateRange.from && reportDateRange.to && (
                    <p><span className="font-bold">Date Range:</span> {reportDateRange.from} to {reportDateRange.to}</p>
                  )}
                  <p className="text-xs text-blue-600 mt-2 italic">Reports will include all HR processed requests and their current status for the selected period.</p>
                </div>
              </div>
            </div>
          </div>
        ) : (
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="p-4 text-[10px] font-bold font-poppins text-slate-500 uppercase tracking-widest">Reporting Manager</th>
                  <th className="p-4 text-[10px] font-bold font-poppins text-slate-500 uppercase tracking-widest">Employee Details</th>
                  <th className="p-4 text-[10px] font-bold font-poppins text-slate-500 uppercase tracking-widest">Request Category</th>
                  <th className="p-4 text-[10px] font-bold font-poppins text-slate-500 uppercase tracking-widest">Data Summary</th>
                  <th className="p-4 text-[10px] font-bold font-poppins text-slate-500 uppercase tracking-widest text-center">Priority</th>
                  <th className="p-4 text-[10px] font-bold font-poppins text-slate-500 uppercase tracking-widest text-right">Final Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 font-roboto">
                {filtered.map((req) => (
                  <tr key={req.id} className="hover:bg-blue-50/20 transition-colors group">
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <div className="h-7 w-7 rounded-full bg-slate-800 flex items-center justify-center text-[10px] font-bold text-white border border-slate-700 uppercase">
                          {req.manager?.charAt(0)}
                        </div>
                        <span className="text-xs font-semibold text-slate-700">{req.manager}</span>
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="flex flex-col">
                        <span className="text-xs font-bold text-slate-900">{req.name}</span>
                        <span className="text-[10px] text-blue-600 font-bold mt-0.5 tracking-wider">{req.empId}</span>
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        {getCategoryIcon(req.category)}
                        <span className={`text-[9px] font-bold px-2 py-0.5 rounded border uppercase ${
                          req.category === 'promotion' 
                          ? 'bg-amber-50 text-amber-700 border-amber-200' 
                          : 'bg-purple-50 text-purple-700 border-purple-100'
                        }`}>
                          {req.type}
                        </span>
                      </div>
                    </td>
                    <td className="p-4">
                      <p className="text-xs text-slate-600 font-medium">{req.details}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-[9px] font-bold text-slate-400 uppercase">Amount: ₹{req.amount}</span>
                        <span className="text-[9px] font-bold text-slate-300">•</span>
                        <span className="text-[9px] font-bold text-slate-400 uppercase">{req.date}</span>
                      </div>
                    </td>
                    <td className="p-4 text-center">
                        <span className={`text-[9px] font-bold ${req.priority === 'High' ? 'text-orange-500' : 'text-slate-400'} uppercase`}>
                          {req.priority}
                        </span>
                    </td>
                    <td className="p-4 text-right">
                      {(activeTab === "pending" || (activeTab === "offboarding" && (req.status === "PENDING_HR_APPROVAL" || req.status === "pending_hr_approval"))) ? (
                        <div className="flex justify-end gap-2">
                          {req.category === 'expense' && req.hasReceipt && (
                            <button 
                              onClick={() => window.open(`/unified_approval/expense/download/${req.id}`, '_blank')}
                              className="p-1.5 bg-blue-50 text-blue-600 hover:bg-blue-100 rounded-md border border-blue-200" 
                              title="Download Receipt"
                            >
                              <DocumentArrowDownIcon className="h-4 w-4 stroke-[2.5]"/>
                            </button>
                          )}
                          {req.category === 'timesheet' && (
                            <>
                              <button 
                                onClick={() => downloadTimesheet(req.id, 'excel')}
                                className="p-1.5 bg-green-50 text-green-600 hover:bg-green-100 rounded-md border border-green-200" 
                                title="Download Excel"
                              >
                                <TableCellsIcon className="h-4 w-4 stroke-[2.5]"/>
                              </button>
                              <button 
                                onClick={() => downloadTimesheet(req.id, 'pdf')}
                                className="p-1.5 bg-red-50 text-red-600 hover:bg-red-100 rounded-md border border-red-200" 
                                title="Download PDF"
                              >
                                <DocumentTextIcon className="h-4 w-4 stroke-[2.5]"/>
                              </button>
                            </>
                          )}
                          <button onClick={() => handleFinalAction(req.id, 'reject')} className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-all">
                            <XMarkIcon className="h-4 w-4 stroke-[2.5]" />
                          </button>
                          <button onClick={() => handleFinalAction(req.id, 'approve')} className="flex items-center gap-2 bg-slate-900 hover:bg-blue-700 text-white px-4 py-2 rounded text-[10px] font-bold font-poppins uppercase tracking-wide transition-all shadow-sm">
                            <CheckIcon className="h-4 w-4" /> Finalize
                          </button>
                        </div>
                      ) : (
                        <div className="text-right">
                          <span className={`text-[9px] font-bold px-2 py-1 rounded uppercase ${
                            req.status === 'HR_APPROVED' 
                            ? 'bg-green-50 text-green-700 border border-green-200' 
                            : 'bg-red-50 text-red-700 border border-red-200'
                          }`}>
                            {req.status === 'HR_APPROVED' ? 'Approved' : 'Rejected'}
                          </span>
                          {req.reason && (
                            <p className="text-[10px] text-slate-500 mt-1">Reason: {req.reason}</p>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            
            {(filtered.length === 0 && !loading) && (
              <div className="py-24 flex flex-col items-center justify-center text-slate-400">
                <ArrowPathIcon className="h-12 w-12 mb-4 opacity-10 animate-spin-slow" />
                <p className="text-[12px] font-bold font-poppins uppercase tracking-[0.2em]">Queue Fully Processed</p>
              </div>
            )}
            
            {loading && (
               <div className="py-24 flex flex-col items-center justify-center">
                <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                <p className="text-[10px] mt-4 font-poppins font-bold uppercase tracking-widest">Syncing Master Database...</p>
             </div>
            )}
          </div>
        </div>
        )}
      </div>

      {/* REJECTION REASON MODAL */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96 max-w-[90vw]">
            <h3 className="text-lg font-bold font-poppins text-slate-900 mb-4">Reject Request</h3>
            <p className="text-sm text-slate-600 mb-4">Please provide a reason for rejecting this request:</p>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              className="w-full p-3 border border-slate-300 rounded-md resize-none h-24 text-sm"
              placeholder="Enter rejection reason..."
            />
            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectReason("");
                  setSelectedRequestId(null);
                }}
                className="px-4 py-2 text-slate-600 hover:text-slate-800 text-sm font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleRejectSubmit}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded text-sm font-medium"
              >
                Reject Request
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HRTerminal;